import type { CatalogueContent } from '~/services/catalogue'
import type { CatalogueGridSlot } from '~/store/catalogue-grid'

// The Classic content filter (GLOSSARY.md): which content kind the Classic
// tab's cards show — Trees or Land skins, exactly one at a time, never an
// "all" view. It opens on Trees.
export type ClassicContentFilter = CatalogueContent

export const DEFAULT_CLASSIC_CONTENT_FILTER: ClassicContentFilter = 'tree_type'

// Chip order: Trees, then Land.
export const CLASSIC_CONTENT_FILTERS: readonly ClassicContentFilter[] = [
  'tree_type',
  'land_skin',
]

// A kind has something to show while any of its slots is not omitted: a
// pending slot may yet hydrate (GLOSSARY.md "Empty Shop collection").
function hasVisibleSlotsOf(
  slots: readonly CatalogueGridSlot[],
  content: CatalogueContent,
): boolean {
  return slots.some(
    (slot) => slot.content === content && slot.status !== 'omitted',
  )
}

// The chips appear only while both kinds have visible slots; with one kind
// the tab shows that kind with no chips.
export function offersClassicContentFilter(
  slots: readonly CatalogueGridSlot[],
): boolean {
  return CLASSIC_CONTENT_FILTERS.every((content) =>
    hasVisibleSlotsOf(slots, content),
  )
}

// The kind actually showing: the chosen one while it has visible slots,
// otherwise the other kind if it has any — a showing kind that loses its
// last visible slot hands the tab over. With nothing to show anywhere the
// choice stands; the grid is empty either way.
export function resolveClassicContentFilter(
  chosen: ClassicContentFilter,
  slots: readonly CatalogueGridSlot[],
): ClassicContentFilter {
  if (hasVisibleSlotsOf(slots, chosen)) return chosen
  return (
    CLASSIC_CONTENT_FILTERS.find(
      (content) => content !== chosen && hasVisibleSlotsOf(slots, content),
    ) ?? chosen
  )
}
